import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

/**
 * Privacy Policy / Datenschutzerklaerung Page
 * GDPR-compliant privacy policy for Story Planner Pro
 * Serves both /datenschutz (DE) and /privacy (EN)
 */
const PrivacyPage = () => {
  const { i18n } = useTranslation()
  const isGerman = i18n.language === 'de'

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-600 hover:text-purple-600 transition-colors mb-4"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {isGerman ? 'Zurück' : 'Back'}
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            {isGerman ? 'Datenschutzerklärung' : 'Privacy Policy'}
          </h1>
          <p className="mt-2 text-gray-600">
            Story Planner Pro
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {isGerman
              ? 'Informationen gemäß Art. 13, 14 DSGVO — Stand: Januar 2026'
              : 'Information pursuant to Art. 13, 14 GDPR — Last updated: January 2026'}
          </p>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-8 space-y-8">

            {/* 1. Verantwortlicher / Controller */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '1. Verantwortlicher' : '1. Controller'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <div className="ml-4 space-y-1">
                  <p className="font-semibold">Emporion Innovations GmbH</p>
                  <p>Ludwigstr. 32</p>
                  <p>63067 Offenbach am Main</p>
                  <p>{isGerman ? 'Deutschland' : 'Germany'}</p>
                  <p className="mt-2">
                    {isGerman ? 'E-Mail' : 'Email'}: <a href="mailto:info@emporion.technology" className="text-purple-600 hover:text-purple-700">info@emporion.technology</a><br/>
                    {isGerman ? 'Telefon' : 'Phone'}: <a href="tel:+4906999998088" className="text-purple-600 hover:text-purple-700">+49 (0) 69 99998088</a>
                  </p>
                  <p className="mt-2">
                    {isGerman ? 'Geschäftsführer' : 'Managing Directors'}: Alexander Kuchta, Dennis Schmitz
                  </p>
                  <p className="mt-2">
                    {isGerman ? 'Registergericht' : 'Commercial Register'}: {isGerman ? 'Amtsgericht Offenbach am Main' : 'District Court Offenbach am Main'}<br/>
                    {isGerman ? 'Registernummer' : 'Registration Number'}: HRB 51283<br/>
                    {isGerman ? 'USt-IdNr.' : 'VAT ID'}: DE284997944
                  </p>
                  <p className="mt-3">
                    <strong>{isGerman ? 'Datenschutzanfragen' : 'Data Protection Inquiries'}:</strong><br/>
                    {isGerman ? 'E-Mail' : 'Email'}: <a href="mailto:datenschutz@storyplannerpro.de" className="text-purple-600 hover:text-purple-700">datenschutz@storyplannerpro.de</a>
                  </p>
                </div>
              </div>
            </section>

            {/* 2. Overview of Data Processing */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '2. Überblick über die Datenverarbeitung' : '2. Overview of Data Processing'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? 'Story Planner Pro ist eine SaaS-Plattform zur Automatisierung von Instagram Stories mit KI-generierten Captions sowie zur Bereitstellung von Link-in-Bio-Seiten. Im Rahmen des Betriebs verarbeiten wir verschiedene Kategorien personenbezogener Daten:'
                    : 'Story Planner Pro is a SaaS platform for automating Instagram Stories with AI-generated captions and providing link-in-bio pages. In the course of our operations, we process the following categories of personal data:'}
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>{isGerman ? 'Benutzerkontodaten' : 'User account data'}</li>
                  <li>{isGerman ? 'Instagram-Account-Informationen' : 'Instagram account information'}</li>
                  <li>{isGerman ? 'Hochgeladene Bilder und Videos' : 'Uploaded images and videos'}</li>
                  <li>{isGerman ? 'KI-generierte Inhalte' : 'AI-generated content'}</li>
                  <li>{isGerman ? 'Produktdaten aus Google Shopping Feeds' : 'Product data from Google Shopping Feeds'}</li>
                  <li>{isGerman ? 'Analytics- und Nutzungsdaten' : 'Analytics and usage data'}</li>
                  <li>{isGerman ? 'Zahlungsinformationen' : 'Payment information'}</li>
                </ul>

                <p className="font-semibold mt-4">
                  {isGerman ? '2.1 Unsere unterschiedlichen Rollen' : '2.1 Our Different Roles'}
                </p>
                <div className="overflow-x-auto mt-2">
                  <table className="min-w-full text-sm border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                          {isGerman ? 'Kontext' : 'Context'}
                        </th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                          {isGerman ? 'Unsere Rolle' : 'Our Role'}
                        </th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                          {isGerman ? 'Beschreibung' : 'Description'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="px-4 py-2">
                          {isGerman ? 'Website storyplannerpro.de' : 'Website storyplannerpro.de'}
                        </td>
                        <td className="px-4 py-2 font-semibold">
                          {isGerman ? 'Verantwortlicher' : 'Controller'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Marketing-Website, Registrierung, Dashboard' : 'Marketing website, registration, dashboard'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-4 py-2">
                          {isGerman ? 'SaaS-Plattform (Nutzerkonto)' : 'SaaS Platform (User Account)'}
                        </td>
                        <td className="px-4 py-2 font-semibold">
                          {isGerman ? 'Verantwortlicher' : 'Controller'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Accountverwaltung, Abrechnung, Support' : 'Account management, billing, support'}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2">
                          {isGerman ? 'Link-in-Bio-Seiten der Nutzer' : 'Users\' link-in-bio pages'}
                        </td>
                        <td className="px-4 py-2 font-semibold">
                          {isGerman ? 'Auftragsverarbeiter' : 'Processor'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Technisches Hosting im Auftrag unserer Nutzer' : 'Technical hosting on behalf of our users'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* 3. Legal Bases */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '3. Rechtsgrundlagen' : '3. Legal Bases'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? 'Die Verarbeitung personenbezogener Daten erfolgt auf Grundlage der folgenden Rechtsgrundlagen:'
                    : 'The processing of personal data is based on the following legal bases:'}
                </p>
                <div className="overflow-x-auto mt-2">
                  <table className="min-w-full text-sm border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                          {isGerman ? 'Rechtsgrundlage' : 'Legal Basis'}
                        </th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                          {isGerman ? 'Anwendungsfall' : 'Use Case'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="px-4 py-2 font-semibold">
                          {isGerman ? 'Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)' : 'Art. 6(1)(b) GDPR (Contractual Necessity)'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Bereitstellung unserer Dienste gemäß Nutzungsvertrag' : 'Provision of our services pursuant to the user agreement'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-4 py-2 font-semibold">
                          {isGerman ? 'Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)' : 'Art. 6(1)(a) GDPR (Consent)'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Verwendung optionaler Cookies, Analytics, Marketing' : 'Use of optional cookies, analytics, marketing'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-4 py-2 font-semibold">
                          {isGerman ? 'Art. 6 Abs. 1 lit. f DSGVO (Berechtigte Interessen)' : 'Art. 6(1)(f) GDPR (Legitimate Interests)'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'IT-Sicherheit, Betrugsprävention, Optimierung' : 'IT security, fraud prevention, optimization'}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-semibold">
                          {isGerman ? 'Art. 6 Abs. 1 lit. c DSGVO (Rechtliche Verpflichtung)' : 'Art. 6(1)(c) GDPR (Legal Obligation)'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Steuerliche Aufbewahrungspflichten' : 'Tax retention obligations'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* 4. Collection and Processing of Personal Data */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '4. Erhebung und Verarbeitung personenbezogener Daten' : '4. Collection and Processing of Personal Data'}
              </h2>
              <div className="space-y-6 text-gray-700 text-sm">

                {/* 4.1 */}
                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2">
                    {isGerman ? '4.1 Benutzerkontodaten' : '4.1 User Account Data'}
                  </h3>
                  <p><strong>{isGerman ? 'Erhobene Daten' : 'Data Collected'}:</strong></p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>{isGerman ? 'E-Mail-Adresse' : 'Email address'}</li>
                    <li>{isGerman ? 'Verschlüsseltes Passwort (nur als Hash)' : 'Encrypted password (stored as hash only)'}</li>
                    <li>{isGerman ? 'Name / Firmenname' : 'Name / company name'}</li>
                    <li>{isGerman ? 'Account-Erstellungsdatum' : 'Account creation date'}</li>
                    <li>{isGerman ? 'Impressumsdaten (für Link-in-Bio-Seiten)' : 'Legal notice data (for link-in-bio pages)'}</li>
                  </ul>
                  <p className="mt-2">
                    <strong>{isGerman ? 'Zweck' : 'Purpose'}:</strong>{' '}
                    {isGerman ? 'Bereitstellung des Dienstes, Authentifizierung, Kundensupport' : 'Service provision, authentication, customer support'}
                  </p>
                  <p>
                    <strong>{isGerman ? 'Rechtsgrundlage' : 'Legal Basis'}:</strong>{' '}
                    {isGerman ? 'Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)' : 'Art. 6(1)(b) GDPR (contractual necessity)'}
                  </p>
                  <p>
                    <strong>{isGerman ? 'Speicherdauer' : 'Retention Period'}:</strong>{' '}
                    {isGerman
                      ? 'Bis zur Löschung des Accounts durch den Nutzer, danach 30 Tage bis zur endgültigen Löschung'
                      : 'Until account deletion by the user, then 30 days until final deletion'}
                  </p>
                  <p>
                    <strong>{isGerman ? 'Dienstleister' : 'Service Provider'}:</strong>{' '}
                    {isGerman
                      ? 'Supabase (USA) – Verarbeitung auf Basis von EU-Standardvertragsklauseln und EU-US Data Privacy Framework'
                      : 'Supabase (USA) – Processing based on EU Standard Contractual Clauses and EU-US Data Privacy Framework'}
                  </p>
                </div>

                {/* 4.2 */}
                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2">
                    {isGerman ? '4.2 Instagram-Account-Daten' : '4.2 Instagram Account Data'}
                  </h3>
                  <p><strong>{isGerman ? 'Erhobene Daten' : 'Data Collected'}:</strong></p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>{isGerman ? 'Instagram-Benutzername und Profil-ID' : 'Instagram username and profile ID'}</li>
                    <li>{isGerman ? 'Profilbild-URL' : 'Profile picture URL'}</li>
                    <li>{isGerman ? 'Access Token (verschlüsselt gespeichert)' : 'Access token (stored encrypted)'}</li>
                    <li>{isGerman ? 'Account-Verifizierungsstatus' : 'Account verification status'}</li>
                  </ul>
                  <p className="mt-2">
                    <strong>{isGerman ? 'Zweck' : 'Purpose'}:</strong>{' '}
                    {isGerman ? 'Automatisches Posten von Stories, Abruf von Analytics-Daten' : 'Automated posting of stories, retrieval of analytics data'}
                  </p>
                  <p>
                    <strong>{isGerman ? 'Rechtsgrundlage' : 'Legal Basis'}:</strong>{' '}
                    {isGerman ? 'Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)' : 'Art. 6(1)(b) GDPR (contractual necessity)'}
                  </p>
                  <p>
                    <strong>{isGerman ? 'Drittanbieter' : 'Third-Party Provider'}:</strong> Meta/Instagram Graph API
                  </p>
                  <p>
                    <strong>{isGerman ? 'Hinweis' : 'Note'}:</strong>{' '}
                    {isGerman
                      ? 'Wir speichern keine Instagram-Inhalte dauerhaft. Die Verarbeitung erfolgt gemäß den Nutzungsbedingungen der Meta Platform.'
                      : 'We do not permanently store Instagram content. Processing is carried out in accordance with Meta Platform Terms of Use.'}
                  </p>
                </div>

                {/* 4.3 */}
                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2">
                    {isGerman ? '4.3 Bild- und Video-Inhalte' : '4.3 Image and Video Content'}
                  </h3>
                  <p><strong>{isGerman ? 'Erhobene Daten' : 'Data Collected'}:</strong></p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>{isGerman ? 'Hochgeladene Bilder und Videos' : 'Uploaded images and videos'}</li>
                    <li>{isGerman ? 'Bildmetadaten (Größe, Format, Upload-Zeitpunkt)' : 'Image metadata (size, format, upload time)'}</li>
                    <li>{isGerman ? 'KI-generierte Bildanalysen und Captions' : 'AI-generated image analyses and captions'}</li>
                    <li>{isGerman ? 'Freigabe- und Ablehnungsentscheidungen' : 'Approval and rejection decisions'}</li>
                  </ul>
                  <p className="mt-2">
                    <strong>{isGerman ? 'Zweck' : 'Purpose'}:</strong>{' '}
                    {isGerman ? 'Generierung von KI-Captions, Story-Planung und -Veröffentlichung' : 'AI caption generation, story scheduling and publishing'}
                  </p>
                  <p>
                    <strong>{isGerman ? 'Rechtsgrundlage' : 'Legal Basis'}:</strong>{' '}
                    {isGerman ? 'Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)' : 'Art. 6(1)(b) GDPR (contractual necessity)'}
                  </p>
                  <p className="mt-2"><strong>{isGerman ? 'Verarbeitung' : 'Processing'}:</strong></p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>
                      <strong>Azure Blob Storage (Microsoft):</strong>{' '}
                      {isGerman ? 'Sichere Speicherung in EU-Rechenzentren' : 'Secure storage in EU data centers'}
                    </li>
                    <li>
                      <strong>OpenAI GPT-4 (USA):</strong>{' '}
                      {isGerman
                        ? 'KI-Analyse zur Caption-Generierung. OpenAI verarbeitet Ihre Daten gemäß ihrer Datenschutzrichtlinie und verwendet keine zur Weiterverwendung übermittelten Daten zum Training ihrer Modelle.'
                        : 'AI analysis for caption generation. OpenAI processes your data in accordance with their privacy policy and does not use data submitted via our API to train their models.'}
                    </li>
                  </ul>
                </div>

                {/* 4.4 */}
                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2">
                    {isGerman ? '4.4 Google Shopping Feed und Produktdaten' : '4.4 Google Shopping Feed and Product Data'}
                  </h3>
                  <p><strong>{isGerman ? 'Erhobene Daten' : 'Data Collected'}:</strong></p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>{isGerman ? 'Feed-URL' : 'Feed URL'}</li>
                    <li>{isGerman ? 'Produktdaten (Titel, Beschreibungen, Preise, Bilder, Links)' : 'Product data (titles, descriptions, prices, images, links)'}</li>
                  </ul>
                  <p className="mt-2">
                    <strong>{isGerman ? 'Zweck' : 'Purpose'}:</strong>{' '}
                    {isGerman ? 'Anzeige von Produkten auf Link-in-Bio-Seiten' : 'Display of products on link-in-bio pages'}
                  </p>
                  <p>
                    <strong>{isGerman ? 'Rechtsgrundlage' : 'Legal Basis'}:</strong>{' '}
                    {isGerman ? 'Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)' : 'Art. 6(1)(b) GDPR (contractual necessity)'}
                  </p>
                </div>

                {/* 4.5 */}
                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2">
                    {isGerman ? '4.5 Zahlungsdaten' : '4.5 Payment Data'}
                  </h3>
                  <p><strong>{isGerman ? 'Erhobene Daten' : 'Data Collected'}:</strong></p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>Stripe Customer-ID</li>
                    <li>{isGerman ? 'Subscription-ID und -Status' : 'Subscription ID and status'}</li>
                    <li>{isGerman ? 'Gewählter Preisplan' : 'Selected pricing plan'}</li>
                    <li>{isGerman ? 'Rechnungsadresse' : 'Billing address'}</li>
                  </ul>
                  <p className="mt-2 font-semibold">
                    {isGerman
                      ? 'Wichtig: Wir speichern KEINE Kreditkartendaten. Alle Zahlungsinformationen werden ausschließlich von Stripe verarbeitet.'
                      : 'Important: We do NOT store any credit card data. All payment information is processed exclusively by Stripe.'}
                  </p>
                  <div className="mt-2 ml-4">
                    <p><strong>{isGerman ? 'Zahlungsdienstleister' : 'Payment Service Provider'}:</strong></p>
                    <p>Stripe Inc.</p>
                    <p>510 Townsend Street, San Francisco, CA 94103, USA</p>
                    <p className="mt-1">
                      {isGerman
                        ? 'Stripe ist PCI-DSS Level 1 zertifiziert und unter dem EU-US Data Privacy Framework zertifiziert. Zusätzlich gelten EU-Standardvertragsklauseln.'
                        : 'Stripe is PCI-DSS Level 1 certified and certified under the EU-US Data Privacy Framework. Additionally, EU Standard Contractual Clauses apply.'}
                    </p>
                    <p className="mt-1">
                      {isGerman ? 'Weitere Informationen' : 'More information'}:{' '}
                      <a href="https://stripe.com/de/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">
                        https://stripe.com/de/privacy
                      </a>
                    </p>
                  </div>
                  <p className="mt-2">
                    <strong>{isGerman ? 'Speicherdauer' : 'Retention Period'}:</strong>{' '}
                    {isGerman
                      ? 'Rechnungsdaten werden gemäß § 147 AO für 10 Jahre aufbewahrt.'
                      : 'Billing data is retained for 10 years pursuant to Section 147 of the German Fiscal Code (AO).'}
                  </p>
                </div>

                {/* 4.6 */}
                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2">
                    {isGerman ? '4.6 Analytics- und Nutzungsdaten' : '4.6 Analytics and Usage Data'}
                  </h3>
                  <p><strong>{isGerman ? 'Erhobene Daten' : 'Data Collected'}:</strong></p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>{isGerman ? 'Story-Performance-Metriken (Impressions, Reach, Taps)' : 'Story performance metrics (impressions, reach, taps)'}</li>
                    <li>{isGerman ? 'Nutzungsstatistiken (hochgeladene Bilder, geplante Stories)' : 'Usage statistics (uploaded images, scheduled stories)'}</li>
                    <li>{isGerman ? 'Feature-Nutzung und Quotenverbrauch' : 'Feature usage and quota consumption'}</li>
                  </ul>
                  <p className="mt-2">
                    <strong>{isGerman ? 'Quelle' : 'Source'}:</strong> Instagram Graph API (Instagram Insights)
                  </p>
                  <p>
                    <strong>{isGerman ? 'Rechtsgrundlage' : 'Legal Basis'}:</strong>{' '}
                    {isGerman ? 'Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)' : 'Art. 6(1)(b) GDPR (contractual necessity)'}
                  </p>
                </div>
              </div>
            </section>

            {/* 5. Data Processing on Link-in-Bio Pages */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '5. Datenverarbeitung auf Link-in-Bio-Seiten' : '5. Data Processing on Link-in-Bio Pages'}
              </h2>
              <div className="space-y-4 text-gray-700 text-sm">
                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2">
                    {isGerman ? '5.1 Unsere Rolle: Auftragsverarbeiter (Art. 28 DSGVO)' : '5.1 Our Role: Processor (Art. 28 GDPR)'}
                  </h3>
                  <p>
                    {isGerman
                      ? <>Für die Link-in-Bio-Seiten, die unsere Nutzer erstellen, sind wir <strong>Auftragsverarbeiter</strong> gemäß Art. 28 DSGVO. Der jeweilige Nutzer (Shop-Betreiber) ist der datenschutzrechtlich <strong>Verantwortliche</strong>.</>
                      : <>For the link-in-bio pages created by our users, we act as a <strong>processor</strong> pursuant to Art. 28 GDPR. The respective user (shop operator) is the <strong>controller</strong> under data protection law.</>}
                  </p>
                  <p className="mt-2">
                    {isGerman
                      ? <>Die Details der Auftragsverarbeitung sind in unserem Auftragsverarbeitungsvertrag (AVV) geregelt:{' '}</>
                      : <>The details of data processing are governed by our Data Processing Agreement (DPA):{' '}</>}
                    <Link to="/avv" className="text-purple-600 hover:text-purple-700 underline">
                      https://storyplannerpro.de/avv
                    </Link>
                  </p>
                </div>

                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2">
                    {isGerman ? '5.2 Welche Daten wir auf Link-in-Bio-Seiten verarbeiten' : '5.2 Data We Process on Link-in-Bio Pages'}
                  </h3>
                  <div className="overflow-x-auto mt-2">
                    <table className="min-w-full text-sm border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                            {isGerman ? 'Daten' : 'Data'}
                          </th>
                          <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                            {isGerman ? 'Speicherung' : 'Storage'}
                          </th>
                          <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                            {isGerman ? 'Anmerkung' : 'Note'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b">
                          <td className="px-4 py-2">
                            {isGerman ? 'IP-Adressen' : 'IP Addresses'}
                          </td>
                          <td className="px-4 py-2 font-semibold">
                            {isGerman ? 'Nein' : 'No'}
                          </td>
                          <td className="px-4 py-2">
                            {isGerman ? 'Werden technisch übertragen, aber nicht gespeichert' : 'Technically transmitted but not stored'}
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="px-4 py-2">
                            {isGerman ? 'Klickzahlen, Seitenaufrufe' : 'Click counts, page views'}
                          </td>
                          <td className="px-4 py-2">
                            {isGerman ? 'Ja, anonymisiert' : 'Yes, anonymized'}
                          </td>
                          <td className="px-4 py-2">
                            {isGerman ? 'Keine personenbezogenen Daten' : 'No personal data'}
                          </td>
                        </tr>
                        <tr>
                          <td className="px-4 py-2">
                            {isGerman ? 'Cookie-Consent-Status' : 'Cookie consent status'}
                          </td>
                          <td className="px-4 py-2">
                            {isGerman ? 'Ja' : 'Yes'}
                          </td>
                          <td className="px-4 py-2">
                            {isGerman ? 'Falls der Nutzer Tracking-Tools einbindet' : 'If the user integrates tracking tools'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2">
                    {isGerman ? '5.3 Keine eigenen Tracking-Tools auf Nutzer-Seiten' : '5.3 No Proprietary Tracking Tools on User Pages'}
                  </h3>
                  <p className="font-semibold">
                    {isGerman
                      ? 'Wir setzen auf den Link-in-Bio-Seiten unserer Nutzer KEINE eigenen Tracking-Tools ein. Insbesondere verwenden wir dort:'
                      : 'We do NOT deploy any proprietary tracking tools on our users\' link-in-bio pages. Specifically, we do not use:'}
                  </p>
                  <ul className="ml-4 mt-2 space-y-1">
                    <li>{isGerman ? '❌ Kein Facebook Pixel' : '❌ No Facebook Pixel'}</li>
                    <li>{isGerman ? '❌ Kein Google Analytics' : '❌ No Google Analytics'}</li>
                    <li>{isGerman ? '❌ Keine sonstigen Analyse- oder Marketing-Tools zu unseren Zwecken' : '❌ No other analytics or marketing tools for our purposes'}</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2">
                    {isGerman ? '5.4 Optionale Tracking-Tools der Nutzer' : '5.4 Optional Tracking Tools by Users'}
                  </h3>
                  <p>
                    {isGerman
                      ? <>Unsere Nutzer können <strong>optional</strong> eigene Tracking-Tools (Facebook Pixel, Google Analytics 4) auf ihren Link-in-Bio-Seiten einbinden. In diesem Fall:</>
                      : <>Our users may <strong>optionally</strong> integrate their own tracking tools (Facebook Pixel, Google Analytics 4) on their link-in-bio pages. In this case:</>}
                  </p>
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>
                      {isGerman
                        ? <>Ist der <strong>Nutzer</strong> (Shop-Betreiber) datenschutzrechtlich verantwortlich</>
                        : <>The <strong>user</strong> (shop operator) is the controller under data protection law</>}
                    </li>
                    <li>
                      {isGerman
                        ? <>Muss der <strong>Nutzer</strong> die Einwilligung der Besucher einholen</>
                        : <>The <strong>user</strong> must obtain consent from visitors</>}
                    </li>
                    <li>
                      {isGerman
                        ? <>Stellen wir eine <strong>Cookie-Consent-Lösung</strong> bereit, die Tracking erst nach Einwilligung aktiviert</>
                        : <>We provide a <strong>cookie consent solution</strong> that activates tracking only after consent is given</>}
                    </li>
                  </ul>
                </div>
              </div>

              {/* Google Analytics with User-ID */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">4.7 {isGerman ? 'Google Analytics mit User-ID-Verknüpfung' : 'Google Analytics with User-ID Tracking'}</h3>
                <div className="text-gray-700 text-sm space-y-3">

                  {/* Summary Box */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 my-4">
                    <p className="font-semibold text-gray-900 mb-2">{isGerman ? 'Zusammenfassung' : 'Summary'}</p>
                    <ul className="space-y-1 text-sm">
                      <li>👥 <strong>{isGerman ? 'Betroffene' : 'Affected'}:</strong> {isGerman ? 'Besucher der Website und registrierte Nutzer' : 'Website visitors and registered users'}</li>
                      <li>🤝 <strong>{isGerman ? 'Zweck' : 'Purpose'}:</strong> {isGerman ? 'Analyse des Nutzerverhaltens, Optimierung unserer Dienste' : 'Analysis of user behavior, optimization of our services'}</li>
                      <li>📓 <strong>{isGerman ? 'Verarbeitete Daten' : 'Processed Data'}:</strong> {isGerman ? 'Pseudonymisierte Nutzungsdaten, Geräte- und Browserinformationen, IP-Adresse (anonymisiert), interne User-ID' : 'Pseudonymized usage data, device and browser information, IP address (anonymized), internal user ID'}</li>
                      <li>📅 <strong>{isGerman ? 'Speicherdauer' : 'Retention'}:</strong> {isGerman ? '14 Monate' : '14 months'}</li>
                      <li>⚖️ <strong>{isGerman ? 'Rechtsgrundlagen' : 'Legal Basis'}:</strong> Art. 6 Abs. 1 lit. a DSGVO ({isGerman ? 'Einwilligung' : 'Consent'})</li>
                    </ul>
                  </div>

                  <p><strong>{isGerman ? 'Technische Beschreibung' : 'Technical Description'}:</strong></p>
                  <p>
                    {isGerman
                      ? 'Wir nutzen Google Analytics 4, einen Webanalysedienst der Google Ireland Limited (Gordon House, Barrow Street, Dublin 4, Irland). Muttergesellschaft ist die Google LLC (1600 Amphitheatre Parkway, Mountain View, CA 94043, USA).'
                      : 'We use Google Analytics 4, a web analytics service provided by Google Ireland Limited (Gordon House, Barrow Street, Dublin 4, Ireland). The parent company is Google LLC (1600 Amphitheatre Parkway, Mountain View, CA 94043, USA).'}
                  </p>

                  <p><strong>{isGerman ? 'User-ID-Funktion' : 'User-ID Feature'}:</strong></p>
                  <p>
                    {isGerman
                      ? 'Sofern Sie eingeloggt sind und über unseren Cookie-Banner eingewilligt haben, verknüpfen wir Ihre Websitebesuche mit einer pseudonymisierten internen Nutzer-ID. Dies ermöglicht uns, Ihr Nutzungsverhalten geräteübergreifend zu analysieren und unsere Dienste gezielt zu verbessern. Die User-ID enthält keine Klarnamen, E-Mail-Adressen oder andere direkt identifizierende Informationen.'
                      : 'If you are logged in and have consented via our cookie banner, we link your website visits with a pseudonymized internal user ID. This enables us to analyze your usage behavior across devices and improve our services accordingly. The User-ID does not contain real names, email addresses, or other directly identifying information.'}
                  </p>

                  <p><strong>{isGerman ? 'Übermittelte Datenkategorien' : 'Categories of Data Transmitted'}:</strong></p>
                  <ul className="list-disc list-inside ml-4">
                    <li>{isGerman ? 'Pseudonymisierte User-ID (interne Kennung ohne Personenbezug)' : 'Pseudonymized User-ID (internal identifier without personal reference)'}</li>
                    <li>{isGerman ? 'Seitenaufrufe und Interaktionen innerhalb der Anwendung' : 'Page views and interactions within the application'}</li>
                    <li>{isGerman ? 'Geräteinformationen (Browsertyp, Betriebssystem, Bildschirmauflösung)' : 'Device information (browser type, operating system, screen resolution)'}</li>
                    <li>{isGerman ? 'Anonymisierte IP-Adresse (letzte Oktette werden entfernt)' : 'Anonymized IP address (last octets are removed)'}</li>
                    <li>{isGerman ? 'Referrer-URL (verweisende Website)' : 'Referrer URL (referring website)'}</li>
                    <li>{isGerman ? 'Zeitpunkt und Dauer des Zugriffs' : 'Time and duration of access'}</li>
                    <li>{isGerman ? 'Nutzungsereignisse (z.B. Feature-Nutzung, Button-Klicks)' : 'Usage events (e.g., feature usage, button clicks)'}</li>
                  </ul>

                  <p><strong>{isGerman ? 'Zweck der Verarbeitung' : 'Purpose of Processing'}:</strong></p>
                  <p>
                    {isGerman
                      ? 'Die geräteübergreifende Analyse hilft uns zu verstehen, wie Nutzer Story Planner Pro über verschiedene Geräte hinweg verwenden. Diese Erkenntnisse nutzen wir ausschließlich zur Verbesserung der Benutzerfreundlichkeit und Funktionalität unserer Plattform.'
                      : 'Cross-device analysis helps us understand how users interact with Story Planner Pro across different devices. We use these insights exclusively to improve the usability and functionality of our platform.'}
                  </p>

                  <p><strong>{isGerman ? 'Speicherung durch Google' : 'Storage by Google'}:</strong></p>
                  <p>
                    {isGerman
                      ? 'Die erhobenen Daten werden für einen Zeitraum von 14 Monaten gespeichert und anschließend automatisch gelöscht. Aggregierte Berichte ohne Personenbezug können länger aufbewahrt werden.'
                      : 'The collected data is stored for a period of 14 months and then automatically deleted. Aggregated reports without personal reference may be retained longer.'}
                  </p>

                  <p><strong>{isGerman ? 'Ihre Kontrollmöglichkeiten' : 'Your Control Options'}:</strong></p>
                  <p>
                    {isGerman
                      ? 'Die Datenerhebung mittels Google Analytics findet nur statt, wenn Sie über unseren Cookie-Banner aktiv eingewilligt haben. Diese Einwilligung können Sie jederzeit widerrufen:'
                      : 'Data collection via Google Analytics only occurs if you have actively consented via our cookie banner. You can revoke this consent at any time:'}
                  </p>
                  <ul className="list-disc list-inside ml-4">
                    <li>{isGerman ? 'Cookie-Einstellungen: Über den Link in der Fußzeile unserer Website' : 'Cookie settings: Via the link in the footer of our website'}</li>
                    <li><a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700">{isGerman ? 'Browser Add-on: Google Analytics Opt-out' : 'Browser add-on: Google Analytics Opt-out'}</a></li>
                    <li><a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700">{isGerman ? 'Google Kontoeinstellungen' : 'Google account settings'}</a></li>
                  </ul>

                  <p><strong>{isGerman ? 'Rechtsgrundlage' : 'Legal Basis'}:</strong></p>
                  <p>
                    {isGerman
                      ? 'Die Verarbeitung erfolgt ausschließlich auf Grundlage Ihrer Einwilligung gemäß Art. 6 Abs. 1 lit. a DSGVO. Ohne Ihre ausdrückliche Zustimmung über den Cookie-Banner erfolgt keine Datenübermittlung an Google Analytics und keine Verknüpfung mit Ihrer User-ID.'
                      : 'Processing is carried out exclusively on the basis of your consent pursuant to Art. 6(1)(a) GDPR. Without your explicit consent via the cookie banner, no data will be transmitted to Google Analytics and no linking with your User-ID will occur.'}
                  </p>

                  <p><strong>{isGerman ? 'Internationale Datenübermittlung' : 'International Data Transfer'}:</strong></p>
                  <p>
                    {isGerman
                      ? 'Google LLC ist unter dem EU-US Data Privacy Framework zertifiziert, was ein angemessenes Datenschutzniveau für Übermittlungen in die USA gewährleistet. Zusätzlich gelten die EU-Standardvertragsklauseln gemäß Art. 46 Abs. 2 lit. c DSGVO.'
                      : 'Google LLC is certified under the EU-US Data Privacy Framework, ensuring an adequate level of data protection for transfers to the USA. Additionally, the EU Standard Contractual Clauses pursuant to Art. 46(2)(c) GDPR apply.'}
                  </p>

                  <p><strong>{isGerman ? 'Weitere Informationen' : 'Further Information'}:</strong></p>
                  <ul className="list-disc list-inside ml-4">
                    <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700">{isGerman ? 'Google Datenschutzerklärung' : 'Google Privacy Policy'}</a></li>
                    <li><a href={isGerman ? 'https://marketingplatform.google.com/about/analytics/terms/de/' : 'https://marketingplatform.google.com/about/analytics/terms/us/'} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700">{isGerman ? 'Google Analytics Nutzungsbedingungen' : 'Google Analytics Terms of Service'}</a></li>
                    <li><a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700">{isGerman ? 'Informationen zur Datennutzung durch Google' : 'Information on data usage by Google'}</a></li>
                    <li><a href="https://www.dataprivacyframework.gov/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700">EU-US Data Privacy Framework</a></li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 6. Cookies */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '6. Cookies und ähnliche Technologien' : '6. Cookies and Similar Technologies'}
              </h2>
              <div className="space-y-4 text-gray-700 text-sm">
                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2">
                    {isGerman ? '6.1 Essenzielle Cookies (erforderlich)' : '6.1 Essential Cookies (Required)'}
                  </h3>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>{isGerman ? 'Session-Management und Authentifizierung' : 'Session management and authentication'}</li>
                    <li>{isGerman ? 'Cookie-Consent-Präferenzen' : 'Cookie consent preferences'}</li>
                    <li>{isGerman ? 'Sicherheitsfunktionen (CSRF-Schutz)' : 'Security features (CSRF protection)'}</li>
                  </ul>
                  <p className="mt-1">
                    <strong>{isGerman ? 'Rechtsgrundlage' : 'Legal Basis'}:</strong>{' '}
                    {isGerman
                      ? '§ 25 Abs. 2 Nr. 2 TDDDG (technisch erforderlich), Art. 6 Abs. 1 lit. f DSGVO'
                      : 'Section 25(2)(2) TDDDG (technically necessary), Art. 6(1)(f) GDPR'}
                  </p>
                </div>

                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2">
                    {isGerman ? '6.2 Analytics-Cookies (optional)' : '6.2 Analytics Cookies (Optional)'}
                  </h3>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>{isGerman ? 'Anonymisierte Nutzungsstatistiken' : 'Anonymized usage statistics'}</li>
                    <li>{isGerman ? 'Performance-Monitoring' : 'Performance monitoring'}</li>
                    <li>{isGerman ? 'Hotjar – Heatmaps und Session-Recordings zur Verbesserung der Benutzerfreundlichkeit' : 'Hotjar – heatmaps and session recordings to improve usability'}</li>
                  </ul>
                  <p className="mt-1">
                    <strong>{isGerman ? 'Rechtsgrundlage' : 'Legal Basis'}:</strong>{' '}
                    {isGerman
                      ? 'Art. 6 Abs. 1 lit. a DSGVO (Einwilligung), § 25 Abs. 1 TDDDG'
                      : 'Art. 6(1)(a) GDPR (consent), Section 25(1) TDDDG'}
                  </p>
                </div>

                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2">
                    {isGerman ? '6.3 Marketing-Cookies (optional)' : '6.3 Marketing Cookies (Optional)'}
                  </h3>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>Meta Pixel / Conversions API</li>
                    <li>{isGerman ? 'Personalisierung von Inhalten' : 'Content personalization'}</li>
                  </ul>
                  <p className="mt-1">
                    <strong>{isGerman ? 'Rechtsgrundlage' : 'Legal Basis'}:</strong>{' '}
                    {isGerman
                      ? 'Art. 6 Abs. 1 lit. a DSGVO (Einwilligung), § 25 Abs. 1 TDDDG'
                      : 'Art. 6(1)(a) GDPR (consent), Section 25(1) TDDDG'}
                  </p>
                </div>
              </div>
            </section>

            {/* 7. Meta Conversions API */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '7. Meta Conversions API und Facebook Pixel' : '7. Meta Conversions API and Facebook Pixel'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm border border-gray-200">
                    <tbody>
                      <tr className="border-b">
                        <td className="px-4 py-2 font-semibold bg-gray-50">
                          {isGerman ? 'Betroffene' : 'Data Subjects'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Besucher der Website storyplannerpro.de' : 'Visitors of the website storyplannerpro.de'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-4 py-2 font-semibold bg-gray-50">
                          {isGerman ? 'Zweck' : 'Purpose'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Optimierung unserer Serviceleistung und Werbekampagnen' : 'Optimization of our services and advertising campaigns'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-4 py-2 font-semibold bg-gray-50">
                          {isGerman ? 'Verarbeitete Daten' : 'Processed Data'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman
                            ? 'Ereignisdaten, Nutzerverhalten, Geräteinformationen, IP-Adresse, E-Mail (gehasht)'
                            : 'Event data, user behavior, device information, IP address, email (hashed)'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-4 py-2 font-semibold bg-gray-50">
                          {isGerman ? 'Speicherdauer' : 'Retention'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman
                            ? 'Kundendaten werden innerhalb von 48 Stunden nach Abgleich gelöscht'
                            : 'Customer data is deleted within 48 hours after matching'}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-semibold bg-gray-50">
                          {isGerman ? 'Rechtsgrundlage' : 'Legal Basis'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)' : 'Art. 6(1)(a) GDPR (consent)'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="font-semibold mt-4">
                  {isGerman
                    ? 'Wichtig: Diese Verarbeitung erfolgt nur auf unserer eigenen Website (storyplannerpro.de), nicht auf den Link-in-Bio-Seiten unserer Nutzer.'
                    : 'Important: This processing takes place only on our own website (storyplannerpro.de), not on our users\' link-in-bio pages.'}
                </p>

                <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2">
                  {isGerman ? 'Technische Beschreibung' : 'Technical Description'}
                </h3>
                <p>
                  {isGerman
                    ? 'Für die Erfolgsmessung unserer Werbeanzeigen setzen wir die Meta Conversions API ein. Dabei handelt es sich um eine serverseitige Schnittstelle, die Nutzerinteraktionen wie Registrierungen oder Abo-Abschlüsse direkt von unserem Server an Meta übermittelt. Im Gegensatz zum browserbasierten Meta-Pixel erfolgt die Datenübertragung hier von Server zu Server, was eine höhere Datenqualität und besseren Datenschutz ermöglicht.'
                    : 'To measure the success of our advertisements, we use the Meta Conversions API. This is a server-side interface that transmits user interactions such as registrations or subscription completions directly from our server to Meta. Unlike the browser-based Meta Pixel, data transmission occurs server-to-server, enabling higher data quality and better data protection.'}
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Supabase (USA)</strong> - {isGerman ? 'Authentifizierung und Nutzerverwaltung' : 'Authentication and user management'}</li>
                  <li><strong>Microsoft Azure (EU)</strong> - {isGerman ? 'Cloud-Hosting, Datenspeicherung, Backend-Funktionen' : 'Cloud hosting, data storage, backend functions'}</li>
                  <li><strong>OpenAI (USA)</strong> - {isGerman ? 'KI-basierte Bildanalyse und Caption-Generierung' : 'AI-based image analysis and caption generation'}</li>
                  <li><strong>Stripe (USA)</strong> - {isGerman ? 'Zahlungsabwicklung' : 'Payment processing'}</li>
                  <li><strong>Meta/Instagram (USA)</strong> - {isGerman ? 'Story-Veröffentlichung, Analytics und Conversion-Tracking (Meta Conversions API)' : 'Story publication, analytics, and conversion tracking (Meta Conversions API)'}</li>
                  <li><strong>Google Ireland Limited / Google LLC (USA)</strong> - {isGerman ? 'Webanalyse mit geräteübergreifender Nutzeranalyse mittels pseudonymisierter User-ID (nur mit Einwilligung)' : 'Web analytics with cross-device user analysis using pseudonymized User-ID (only with consent)'}</li>
                  <li><strong>Hotjar Ltd. (Malta/EU)</strong> - {isGerman ? 'Analyse des Nutzerverhaltens durch Heatmaps und Session-Recordings (nur mit Einwilligung)' : 'User behavior analysis through heatmaps and session recordings (only with consent)'}</li>
                </ul>
                <p className="mt-4">
                  {isGerman
                    ? <>Verantwortlich für die Datenverarbeitung in Europa ist die <strong>Meta Platforms Ireland Limited</strong> (Grand Canal Square, Dublin 2, Irland). Das Mutterunternehmen Meta Platforms Inc. hat seinen Sitz in den USA.</>
                    : <>The entity responsible for data processing in Europe is <strong>Meta Platforms Ireland Limited</strong> (Grand Canal Square, Dublin 2, Ireland). The parent company Meta Platforms Inc. is based in the USA.</>}
                </p>

                <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2">
                  {isGerman ? 'Einsatzzweck bei Story Planner Pro' : 'Purpose at Story Planner Pro'}
                </h3>
                <p>
                  {isGerman
                    ? 'Der Einsatz dieser Technologie hilft uns zu verstehen, welche Marketingkanäle neue Nutzer zu uns führen. So können wir unser Werbebudget gezielter einsetzen und Story Planner Pro kontinuierlich verbessern. Die gewonnenen Erkenntnisse nutzen wir ausschließlich zur Optimierung unserer eigenen Dienste.'
                    : 'The use of this technology helps us understand which marketing channels bring new users to our platform. This allows us to allocate our advertising budget more effectively and continuously improve Story Planner Pro. The insights gained are used exclusively to optimize our own services.'}
                </p>

                <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2">
                  {isGerman ? 'Übermittelte Datenkategorien' : 'Categories of Data Transmitted'}
                </h3>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>
                    <strong>{isGerman ? 'Konversions-Events' : 'Conversion Events'}:</strong>{' '}
                    {isGerman
                      ? 'Art der Aktion (z.B. Registrierung, Abo-Abschluss), Zeitstempel'
                      : 'Type of action (e.g. registration, subscription completion), timestamp'}
                  </li>
                  <li>
                    <strong>{isGerman ? 'Pseudonymisierte Nutzerdaten' : 'Pseudonymized User Data'}:</strong>{' '}
                    {isGerman
                      ? 'E-Mail und interne Nutzer-ID werden vor der Übermittlung mittels SHA256 kryptografisch gehasht'
                      : 'Email and internal user ID are cryptographically hashed via SHA256 before transmission'}
                  </li>
                  <li>
                    <strong>{isGerman ? 'Technische Daten' : 'Technical Data'}:</strong>{' '}
                    {isGerman
                      ? 'IP-Adresse, Browser-Kennung, Betriebssystem, Bildschirmgröße'
                      : 'IP address, browser identifier, operating system, screen size'}
                  </li>
                  <li>
                    <strong>{isGerman ? 'Meta-spezifische Kennungen' : 'Meta-Specific Identifiers'}:</strong>{' '}
                    {isGerman
                      ? 'Click-ID aus Werbeanzeigen (fbclid), Browser-Cookie (fbp)'
                      : 'Ad click ID (fbclid), browser cookie (fbp)'}
                  </li>
                  <li>
                    <strong>{isGerman ? 'Kontextdaten' : 'Context Data'}:</strong>{' '}
                    {isGerman ? 'URL der besuchten Seite' : 'URL of the visited page'}
                  </li>
                </ul>

                <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2">
                  {isGerman ? 'Speicherung durch Meta' : 'Storage by Meta'}
                </h3>
                <p>
                  {isGerman
                    ? 'Nach Angaben von Meta werden die übermittelten Kundendaten nach dem Abgleich mit bestehenden Nutzerprofilen binnen 48 Stunden aus den Systemen entfernt. Meta betreibt Server weltweit; aggregierte Statistiken können länger aufbewahrt werden.'
                    : 'According to Meta, the transmitted customer data is removed from their systems within 48 hours after matching with existing user profiles. Meta operates servers worldwide; aggregated statistics may be retained for longer periods.'}
                </p>

                <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2">
                  {isGerman ? 'Gemeinsame Verantwortlichkeit' : 'Joint Controllership'}
                </h3>
                <p>
                  {isGerman
                    ? <>Für die Erhebung und Übermittlung der Daten an Meta besteht eine gemeinsame Verantwortlichkeit gemäß Art. 26 DSGVO. Das Addendum zur gemeinsamen Verantwortlichkeit ist hier abrufbar:{' '}</>
                    : <>For the collection and transmission of data to Meta, a joint controllership exists pursuant to Art. 26 GDPR. The joint controllership addendum is available here:{' '}</>}
                  <a href="https://www.facebook.com/legal/controller_addendum" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">
                    https://www.facebook.com/legal/controller_addendum
                  </a>
                </p>

                <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2">
                  {isGerman ? 'Ihre Kontrollmöglichkeiten' : 'Your Control Options'}
                </h3>
                <p>
                  {isGerman
                    ? 'Die Datenerhebung findet nur statt, wenn Sie über unseren Cookie-Banner zugestimmt haben. Diese Zustimmung können Sie jederzeit über die Cookie-Einstellungen in der Fußzeile unserer Website zurückziehen.'
                    : 'Data collection only takes place if you have consented via our cookie banner. You may withdraw your consent at any time via the cookie settings in the footer of our website.'}
                </p>
                <p className="mt-2">
                  {isGerman ? 'Zusätzlich bietet Meta eigene Einstellungsmöglichkeiten:' : 'Additionally, Meta offers its own settings:'}
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>
                    {isGerman ? 'Meta Werbeeinstellungen' : 'Meta Ad Settings'}:{' '}
                    <a href="https://www.facebook.com/settings/?tab=ads" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">
                      https://www.facebook.com/settings/?tab=ads
                    </a>
                  </li>
                  <li>
                    {isGerman ? 'Europäische Opt-Out-Plattform' : 'European Opt-Out Platform'}:{' '}
                    <a href="https://www.youronlinechoices.com/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">
                      https://www.youronlinechoices.com/
                    </a>
                  </li>
                </ul>

                <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2">
                  {isGerman ? 'Internationale Datenübermittlung' : 'International Data Transfer'}
                </h3>
                <p>
                  {isGerman
                    ? <>Meta Platforms Inc. nimmt am <strong>EU-US Data Privacy Framework</strong> teil und ist entsprechend zertifiziert. Dies gewährleistet ein angemessenes Datenschutzniveau für Übermittlungen in die USA. Darüber hinaus gelten die EU-Standardvertragsklauseln (Art. 46 Abs. 2 lit. c DSGVO).</>
                    : <>Meta Platforms Inc. participates in the <strong>EU-US Data Privacy Framework</strong> and is accordingly certified. This ensures an adequate level of data protection for transfers to the USA. Additionally, EU Standard Contractual Clauses apply (Art. 46(2)(c) GDPR).</>}
                </p>
                <p className="mt-2"><strong>{isGerman ? 'Weitere Informationen' : 'Further Information'}:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>
                    {isGerman ? 'Meta-Datenschutzrichtlinie' : 'Meta Privacy Policy'}:{' '}
                    <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">
                      https://www.facebook.com/privacy/policy/
                    </a>
                  </li>
                  <li>
                    {isGerman ? 'Meta Datenverarbeitungsbedingungen' : 'Meta Data Processing Terms'}:{' '}
                    <a href="https://www.facebook.com/legal/terms/dataprocessing" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">
                      https://www.facebook.com/legal/terms/dataprocessing
                    </a>
                  </li>
                  <li>
                    EU-US Data Privacy Framework:{' '}
                    <a href="https://www.dataprivacyframework.gov/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">
                      https://www.dataprivacyframework.gov/
                    </a>
                  </li>
                </ul>
              </div>
            </section>

            {/* 8. Hotjar */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                8. Hotjar
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? <>Wir nutzen Hotjar zur Analyse des Nutzerverhaltens auf unserer Website (storyplannerpro.de) – <strong>nicht auf den Link-in-Bio-Seiten unserer Nutzer</strong>.</>
                    : <>We use Hotjar to analyze user behavior on our website (storyplannerpro.de) – <strong>not on our users&apos; link-in-bio pages</strong>.</>}
                </p>
                <div className="ml-4 mt-2">
                  <p><strong>{isGerman ? 'Anbieter' : 'Provider'}:</strong></p>
                  <p>Hotjar Ltd.</p>
                  <p>Dragonara Business Centre, 5th Floor, Dragonara Road, Paceville St Julian&apos;s STJ 3141, Malta</p>
                </div>
                <p className="mt-2"><strong>{isGerman ? 'Verarbeitete Daten' : 'Processed Data'}:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>{isGerman ? 'Mausbewegungen, Klicks, Scrollverhalten (Heatmaps)' : 'Mouse movements, clicks, scroll behavior (heatmaps)'}</li>
                  <li>{isGerman ? 'Session-Recordings (anonymisiert)' : 'Session recordings (anonymized)'}</li>
                  <li>{isGerman ? 'Geräteinformationen' : 'Device information'}</li>
                </ul>
                <p className="mt-2">
                  <strong>{isGerman ? 'Rechtsgrundlage' : 'Legal Basis'}:</strong>{' '}
                  {isGerman ? 'Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)' : 'Art. 6(1)(a) GDPR (consent)'}
                </p>
                <p className="mt-2"><strong>{isGerman ? 'Datenschutz-Maßnahmen' : 'Data Protection Measures'}:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>{isGerman ? 'IP-Adressen werden anonymisiert' : 'IP addresses are anonymized'}</li>
                  <li>{isGerman ? 'Formularfelder werden automatisch maskiert' : 'Form fields are automatically masked'}</li>
                  <li>{isGerman ? 'Sensible Daten werden nicht erfasst' : 'Sensitive data is not captured'}</li>
                </ul>
                <p className="mt-2">
                  <strong>{isGerman ? 'Widerspruch' : 'Opt-Out'}:</strong>{' '}
                  {isGerman
                    ? 'Sie können die Erfassung durch Hotjar verhindern, indem Sie Ihre Einwilligung im Cookie-Banner widerrufen oder den Hotjar Opt-Out nutzen: '
                    : 'You can prevent data collection by Hotjar by withdrawing your consent in the cookie banner or using the Hotjar opt-out: '}
                  <a href="https://www.hotjar.com/legal/compliance/opt-out" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">
                    https://www.hotjar.com/legal/compliance/opt-out
                  </a>
                </p>
                <p className="mt-1">
                  {isGerman ? 'Weitere Informationen' : 'More information'}:{' '}
                  <a href="https://www.hotjar.com/legal/policies/privacy/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">
                    https://www.hotjar.com/legal/policies/privacy/
                  </a>
                </p>
              </div>
            </section>

            {/* 9. OpenAI */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '9. OpenAI (KI-Verarbeitung)' : '9. OpenAI (AI Processing)'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? 'Für die Generierung von KI-Captions nutzen wir die API von OpenAI.'
                    : 'We use the OpenAI API for generating AI captions.'}
                </p>
                <div className="ml-4 mt-2">
                  <p><strong>{isGerman ? 'Anbieter' : 'Provider'}:</strong></p>
                  <p>OpenAI, L.L.C.</p>
                  <p>3180 18th Street, San Francisco, CA 94110, USA</p>
                </div>
                <p className="mt-2"><strong>{isGerman ? 'Verarbeitete Daten' : 'Processed Data'}:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>{isGerman ? 'Hochgeladene Bilder (zur Analyse)' : 'Uploaded images (for analysis)'}</li>
                  <li>{isGerman ? 'Generierte Captions' : 'Generated captions'}</li>
                </ul>
                <p className="mt-2">
                  <strong>{isGerman ? 'Zweck' : 'Purpose'}:</strong>{' '}
                  {isGerman ? 'KI-basierte Bildanalyse und Caption-Generierung' : 'AI-based image analysis and caption generation'}
                </p>
                <p>
                  <strong>{isGerman ? 'Rechtsgrundlage' : 'Legal Basis'}:</strong>{' '}
                  {isGerman ? 'Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)' : 'Art. 6(1)(b) GDPR (contractual necessity)'}
                </p>
                <p className="mt-2 font-semibold">
                  {isGerman
                    ? 'Wichtiger Hinweis: Wir nutzen die OpenAI API mit deaktiviertem Training. OpenAI verwendet keine über unsere API übermittelten Daten zum Training ihrer Modelle. Die Daten werden nach der Verarbeitung nicht dauerhaft bei OpenAI gespeichert.'
                    : 'Important Note: We use the OpenAI API with training disabled. OpenAI does not use any data submitted via our API to train their models. Data is not permanently stored by OpenAI after processing.'}
                </p>
                <p className="mt-2">
                  <strong>{isGerman ? 'Internationale Datenübermittlung' : 'International Data Transfer'}:</strong>{' '}
                  {isGerman
                    ? 'OpenAI ist unter dem EU-US Data Privacy Framework zertifiziert. Zusätzlich gelten EU-Standardvertragsklauseln.'
                    : 'OpenAI is certified under the EU-US Data Privacy Framework. Additionally, EU Standard Contractual Clauses apply.'}
                </p>
                <p className="mt-1">
                  {isGerman ? 'Weitere Informationen' : 'More information'}:{' '}
                  <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">
                    https://openai.com/policies/privacy-policy
                  </a>
                </p>
              </div>
            </section>

            {/* 9a. KI-Training */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '9a. Verwendung von Daten zur Verbesserung unserer KI-Dienste' : '9a. Use of Data for Improving Our AI Services'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? 'Wir verwenden die im Rahmen des Dienstes erzeugten KI-generierten Captions, die zugehörigen Prompts, Konfigurationsdaten (z.B. Tonalitätseinstellungen, Sprachpräferenzen) sowie pseudonymisierte Nutzungsdaten (z.B. aggregierte Klickzahlen auf Link-in-Bio-Seiten) zur Qualitätsverbesserung unserer KI-gestützten Dienste.'
                    : 'We use the AI-generated captions created through the Service, the associated prompts, configuration data (e.g., tone of voice settings, language preferences), as well as pseudonymized usage data (e.g., aggregated click counts on Link-in-Bio pages) for the quality improvement of our AI-powered services.'}
                </p>
                <p className="mt-2"><strong>{isGerman ? 'Verarbeitete Daten' : 'Processed Data'}:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>{isGerman ? 'KI-generierte Captions (Text)' : 'AI-generated captions (text)'}</li>
                  <li>{isGerman ? 'System-Prompts und Prompt-Templates' : 'System prompts and prompt templates'}</li>
                  <li>{isGerman ? 'Konfigurationsdaten (Tonalität, Sprache, Emoji-Einstellungen)' : 'Configuration data (tone of voice, language, emoji settings)'}</li>
                  <li>{isGerman ? 'Tone-of-Voice-Beispiele' : 'Tone of voice examples'}</li>
                  <li>{isGerman ? 'Pseudonymisierte Nutzungsdaten (aggregierte Klickzahlen, Seitenaufrufe)' : 'Pseudonymized usage data (aggregated click counts, page views)'}</li>
                </ul>
                <p className="mt-2">
                  <strong>{isGerman ? 'Nicht verarbeitete Daten' : 'Data Not Processed'}:</strong>
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>{isGerman ? 'Keine personenbezogenen Nutzerdaten (Name, E-Mail, Zugangsdaten)' : 'No personal user data (name, email, credentials)'}</li>
                  <li>{isGerman ? 'Keine Bildinhalte' : 'No image content'}</li>
                  <li>{isGerman ? 'Keine Instagram-Account-Informationen' : 'No Instagram account information'}</li>
                </ul>
                <p className="mt-2">
                  <strong>{isGerman ? 'Pseudonymisierung' : 'Pseudonymization'}:</strong>{' '}
                  {isGerman
                    ? 'Vor der Verwendung für Trainingszwecke werden alle Daten pseudonymisiert. Nutzer-IDs, Account-IDs und sonstige identifizierende Merkmale werden entfernt oder durch nicht-rückführbare Kennungen ersetzt. Ein Rückschluss auf den Nutzer ist ohne zusätzliche, getrennt aufbewahrte Informationen nicht möglich.'
                    : 'Before use for training purposes, all data is pseudonymized. User IDs, account IDs, and other identifying attributes are removed or replaced with non-traceable identifiers. Identification of the User is not possible without additional information stored separately.'}
                </p>
                <p className="mt-2">
                  <strong>{isGerman ? 'Zweck' : 'Purpose'}:</strong>{' '}
                  {isGerman
                    ? 'Qualitätsverbesserung der KI-generierten Captions für alle Nutzer des Dienstes'
                    : 'Quality improvement of AI-generated captions for all users of the Service'}
                </p>
                <p>
                  <strong>{isGerman ? 'Rechtsgrundlage' : 'Legal Basis'}:</strong>{' '}
                  {isGerman
                    ? 'Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse). Unser berechtigtes Interesse besteht in der kontinuierlichen Verbesserung der Dienstqualität.'
                    : 'Art. 6(1)(f) GDPR (legitimate interest). Our legitimate interest consists in the continuous improvement of service quality.'}
                </p>
                <p className="mt-2">
                  <strong>{isGerman ? 'Speicherdauer' : 'Retention Period'}:</strong>{' '}
                  {isGerman
                    ? 'Pseudonymisierte Trainingsdaten werden für die Dauer von maximal 24 Monaten ab Erhebung aufbewahrt. Die Erforderlichkeit der Aufbewahrung wird regelmäßig, mindestens jedoch jährlich, überprüft. Nach Erreichen des Verarbeitungszwecks oder Ablauf der Aufbewahrungsfrist werden die Daten gelöscht.'
                    : 'Pseudonymized training data is retained for a maximum of 24 months from the date of collection. The necessity of retention is reviewed regularly, at least annually. Upon achievement of the processing purpose or expiry of the retention period, the data is deleted.'}
                </p>
                <p className="mt-2">
                  <strong>{isGerman ? 'Kein individuelles Profiling' : 'No Individual Profiling'}:</strong>{' '}
                  {isGerman
                    ? 'Es erfolgt kein individuelles Profiling einzelner Nutzer. Die Daten werden ausschließlich in aggregierter Form zur allgemeinen Modellverbesserung herangezogen. Individuelle Nutzungsmuster oder Verhaltensprofile werden nicht erstellt.'
                    : 'No individual profiling of users takes place. The data is used exclusively in aggregated form for general model improvement. Individual usage patterns or behavioral profiles are not created.'}
                </p>
                <p className="mt-2 font-semibold">
                  <strong>{isGerman ? 'Widerspruchsrecht' : 'Right to Object'}:</strong>{' '}
                  {isGerman
                    ? 'Sie können der Verwendung Ihrer Daten für Trainingszwecke jederzeit widersprechen, indem Sie eine E-Mail an '
                    : 'You may object to the use of your data for training purposes at any time by sending an email to '}
                  <a href="mailto:info@storyplannerpro.de" className="text-purple-600 hover:text-purple-700">info@storyplannerpro.de</a>
                  {isGerman
                    ? ' senden. Nach Eingang Ihres Widerspruchs werden keine weiteren Daten von Ihnen für Trainingszwecke verwendet. Bereits pseudonymisierte und in Trainingsdatensätze aufgenommene Daten können technisch nicht nachträglich entfernt werden.'
                    : '. Upon receipt of your objection, no further data from you will be used for training purposes. Data that has already been pseudonymized and incorporated into training datasets cannot be retroactively removed for technical reasons.'}
                </p>
              </div>
            </section>

            {/* 10. Brevo */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '10. Brevo (E-Mail-Versand)' : '10. Brevo (Email Service)'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? 'Für den Versand von transaktionalen E-Mails (Registrierungsbestätigung, Passwort-Reset, Rechnungen, Benachrichtigungen) sowie ggf. Newsletter nutzen wir Brevo (ehemals Sendinblue).'
                    : 'For sending transactional emails (registration confirmation, password reset, invoices, notifications) and, where applicable, newsletters, we use Brevo (formerly Sendinblue).'}
                </p>
                <div className="ml-4 mt-2">
                  <p><strong>{isGerman ? 'Anbieter' : 'Provider'}:</strong></p>
                  <p>Brevo (Sendinblue GmbH)</p>
                  <p>{isGerman ? 'Köpenicker Str. 126, 10179 Berlin, Deutschland' : 'Köpenicker Str. 126, 10179 Berlin, Germany'}</p>
                  <p className="text-xs text-gray-500">
                    {isGerman ? '(Muttergesellschaft: Sendinblue SAS, Paris, Frankreich)' : '(Parent company: Sendinblue SAS, Paris, France)'}
                  </p>
                </div>
                <p className="mt-2"><strong>{isGerman ? 'Verarbeitete Daten' : 'Processed Data'}:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>{isGerman ? 'E-Mail-Adresse' : 'Email address'}</li>
                  <li>{isGerman ? 'Name (falls angegeben)' : 'Name (if provided)'}</li>
                  <li>{isGerman ? 'Zeitpunkt und Status der E-Mail-Zustellung (geöffnet, geklickt)' : 'Time and status of email delivery (opened, clicked)'}</li>
                  <li>{isGerman ? 'IP-Adresse beim Öffnen (für Statistiken)' : 'IP address upon opening (for statistics)'}</li>
                </ul>
                <p className="mt-2">
                  <strong>{isGerman ? 'Standort' : 'Location'}:</strong>{' '}
                  {isGerman
                    ? 'Brevo ist ein EU-Anbieter mit Sitz in Deutschland/Frankreich. Die Datenverarbeitung erfolgt ausschließlich in der EU.'
                    : 'Brevo is an EU provider based in Germany/France. Data processing takes place exclusively within the EU.'}
                </p>
                <p className="mt-1">
                  {isGerman ? 'Weitere Informationen' : 'More information'}:{' '}
                  <a href="https://www.brevo.com/de/legal/privacypolicy/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">
                    https://www.brevo.com/de/legal/privacypolicy/
                  </a>
                </p>
              </div>
            </section>

            {/* 11. Third-Party Disclosure and Processors */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '11. Weitergabe an Dritte und Auftragsverarbeiter' : '11. Third-Party Disclosure and Processors'}
              </h2>
              <div className="space-y-4 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? 'Wir geben Ihre personenbezogenen Daten nur an Dritte weiter, soweit dies zur Erfüllung unserer Dienste erforderlich ist oder eine gesetzliche Grundlage besteht.'
                    : 'We only disclose your personal data to third parties to the extent necessary for the provision of our services or where there is a legal basis.'}
                </p>

                <h3 className="text-base font-semibold text-gray-800 mb-2">
                  {isGerman ? '11.1 Auftragsverarbeiter' : '11.1 Processors'}
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">
                          {isGerman ? 'Anbieter' : 'Provider'}
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">
                          {isGerman ? 'Sitz' : 'Location'}
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">
                          {isGerman ? 'Zweck' : 'Purpose'}
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">
                          {isGerman ? 'Garantien' : 'Safeguards'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="px-3 py-2 font-semibold">Microsoft (Azure)</td>
                        <td className="px-3 py-2">USA</td>
                        <td className="px-3 py-2">
                          {isGerman ? 'Cloud-Hosting, Datenspeicherung, Backend' : 'Cloud hosting, data storage, backend'}
                        </td>
                        <td className="px-3 py-2">
                          {isGerman ? 'EU-Rechenzentren, EU-US DPF, SCCs' : 'EU data centers, EU-US DPF, SCCs'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-3 py-2 font-semibold">Supabase Inc.</td>
                        <td className="px-3 py-2">USA</td>
                        <td className="px-3 py-2">
                          {isGerman ? 'Datenbank, Authentifizierung' : 'Database, authentication'}
                        </td>
                        <td className="px-3 py-2">
                          {isGerman ? 'EU-Region (Frankfurt), EU-US DPF, SCCs' : 'EU region (Frankfurt), EU-US DPF, SCCs'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-3 py-2 font-semibold">Stripe Inc.</td>
                        <td className="px-3 py-2">USA</td>
                        <td className="px-3 py-2">
                          {isGerman ? 'Zahlungsabwicklung' : 'Payment processing'}
                        </td>
                        <td className="px-3 py-2">PCI-DSS Level 1, EU-US DPF, SCCs</td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-3 py-2 font-semibold">OpenAI, L.L.C.</td>
                        <td className="px-3 py-2">USA</td>
                        <td className="px-3 py-2">
                          {isGerman ? 'KI-Caption-Generierung' : 'AI caption generation'}
                        </td>
                        <td className="px-3 py-2">
                          {isGerman ? 'EU-US DPF, SCCs, kein Training' : 'EU-US DPF, SCCs, no training'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-3 py-2 font-semibold">Hotjar Ltd.</td>
                        <td className="px-3 py-2">Malta (EU)</td>
                        <td className="px-3 py-2">
                          {isGerman ? 'Nutzeranalyse' : 'User analytics'}
                        </td>
                        <td className="px-3 py-2">
                          {isGerman ? 'EU-Anbieter' : 'EU provider'}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 font-semibold">Brevo (Sendinblue)</td>
                        <td className="px-3 py-2">
                          {isGerman ? 'Frankreich (EU)' : 'France (EU)'}
                        </td>
                        <td className="px-3 py-2">
                          {isGerman ? 'E-Mail-Versand' : 'Email delivery'}
                        </td>
                        <td className="px-3 py-2">
                          {isGerman ? 'EU-Anbieter, DSGVO-konform' : 'EU provider, GDPR-compliant'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2">
                  {isGerman ? '11.2 Sonstige Empfänger' : '11.2 Other Recipients'}
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                          {isGerman ? 'Empfänger' : 'Recipient'}
                        </th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                          {isGerman ? 'Zweck' : 'Purpose'}
                        </th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                          {isGerman ? 'Rechtsgrundlage' : 'Legal Basis'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="px-4 py-2 font-semibold">Meta/Instagram</td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Story-Veröffentlichung, Analytics, Conversion-Tracking' : 'Story publishing, analytics, conversion tracking'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Art. 6 Abs. 1 lit. b + a DSGVO' : 'Art. 6(1)(b) + (a) GDPR'}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-semibold">
                          {isGerman ? 'Finanzbehörden' : 'Tax Authorities'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Gesetzliche Aufbewahrungspflichten' : 'Statutory retention obligations'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Art. 6 Abs. 1 lit. c DSGVO' : 'Art. 6(1)(c) GDPR'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* 12. International Data Transfers */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '12. Datenübermittlung in Drittländer' : '12. International Data Transfers'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? 'Eine Übermittlung personenbezogener Daten in die USA erfolgt an:'
                    : 'Personal data is transferred to the USA to the following recipients:'}
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Microsoft Corporation (Azure)</li>
                  <li>Supabase Inc.</li>
                  <li>Stripe Inc.</li>
                  <li>OpenAI, L.L.C.</li>
                  <li>Meta Platforms, Inc. ({isGerman ? 'nur mit Einwilligung' : 'with consent only'})</li>
                </ul>

                <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2">
                  {isGerman ? 'Garantien' : 'Safeguards'}
                </h3>
                <p>
                  <strong>EU-US Data Privacy Framework:</strong>{' '}
                  {isGerman
                    ? 'Alle genannten US-Unternehmen sind unter dem EU-US Data Privacy Framework zertifiziert. Die Europäische Kommission hat am 10. Juli 2023 einen Angemessenheitsbeschluss erlassen (Durchführungsbeschluss (EU) 2023/1795).'
                    : 'All aforementioned US companies are certified under the EU-US Data Privacy Framework. The European Commission adopted an adequacy decision on July 10, 2023 (Implementing Decision (EU) 2023/1795).'}
                </p>
                <p className="mt-2">
                  <strong>{isGerman ? 'Standardvertragsklauseln' : 'Standard Contractual Clauses'}:</strong>{' '}
                  {isGerman
                    ? 'Zusätzlich haben wir mit allen Anbietern die EU-Standardvertragsklauseln gemäß EU-Durchführungsbeschluss 2021/914 vereinbart.'
                    : 'Additionally, we have entered into EU Standard Contractual Clauses pursuant to EU Implementing Decision 2021/914 with all providers.'}
                </p>
                <p className="mt-2">
                  <strong>{isGerman ? 'Ergänzende Maßnahmen' : 'Supplementary Measures'}:</strong>{' '}
                  {isGerman
                    ? 'Die primäre Datenverarbeitung erfolgt, wo möglich, in EU-Rechenzentren (Microsoft Azure: West Europe/Germany West Central; Supabase: Frankfurt).'
                    : 'Primary data processing takes place in EU data centers where possible (Microsoft Azure: West Europe/Germany West Central; Supabase: Frankfurt).'}
                </p>
              </div>
            </section>

            {/* 13. Retention Periods */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '13. Speicherdauer' : '13. Retention Periods'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                          {isGerman ? 'Datenkategorie' : 'Data Category'}
                        </th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                          {isGerman ? 'Speicherdauer' : 'Retention Period'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="px-4 py-2">
                          {isGerman ? 'Aktive Accounts' : 'Active accounts'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Solange der Account aktiv ist' : 'As long as the account is active'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-4 py-2">
                          {isGerman ? 'Gelöschte Accounts' : 'Deleted accounts'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Vollständige Löschung innerhalb von 30 Tagen' : 'Complete deletion within 30 days'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-4 py-2">
                          {isGerman ? 'Backup-Daten' : 'Backup data'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Automatische Löschung nach 90 Tagen' : 'Automatic deletion after 90 days'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-4 py-2">
                          {isGerman ? 'Rechnungsdaten' : 'Billing data'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? '10 Jahre (§ 147 AO, § 257 HGB)' : '10 years (Section 147 AO, Section 257 HGB)'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-4 py-2">
                          {isGerman ? 'Support-Anfragen' : 'Support requests'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? '3 Jahre nach Abschluss' : '3 years after resolution'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-4 py-2">
                          {isGerman ? 'Analytics-Daten (Meta, Hotjar)' : 'Analytics data (Meta, Hotjar)'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Gemäß Anbieter-Richtlinien' : 'According to provider policies'}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2">
                          {isGerman ? 'Anonymisierte Statistiken (Link-in-Bio)' : 'Anonymized statistics (link-in-bio)'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Unbegrenzt (keine personenbezogenen Daten)' : 'Indefinite (no personal data)'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="mt-2">
                  {isGerman
                    ? 'Nach Ablauf der Speicherdauer werden die Daten gelöscht oder anonymisiert, sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen.'
                    : 'Upon expiration of the retention period, data is deleted or anonymized, unless statutory retention obligations apply.'}
                </p>
              </div>
            </section>

            {/* 14. Your Rights */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '14. Ihre Rechte' : '14. Your Rights'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? 'Sie haben gemäß DSGVO folgende Rechte in Bezug auf Ihre personenbezogenen Daten:'
                    : 'Under the GDPR, you have the following rights with regard to your personal data:'}
                </p>
                <div className="overflow-x-auto mt-2">
                  <table className="min-w-full text-sm border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                          {isGerman ? 'Recht' : 'Right'}
                        </th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                          {isGerman ? 'Beschreibung' : 'Description'}
                        </th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                          {isGerman ? 'Rechtsgrundlage' : 'Legal Basis'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="px-4 py-2 font-semibold">
                          {isGerman ? 'Auskunft' : 'Access'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman
                            ? 'Sie können Auskunft über Ihre gespeicherten Daten verlangen'
                            : 'You may request information about your stored data'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Art. 15 DSGVO' : 'Art. 15 GDPR'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-4 py-2 font-semibold">
                          {isGerman ? 'Berichtigung' : 'Rectification'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman
                            ? 'Sie können die Korrektur unrichtiger Daten verlangen'
                            : 'You may request the correction of inaccurate data'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Art. 16 DSGVO' : 'Art. 16 GDPR'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-4 py-2 font-semibold">
                          {isGerman ? 'Löschung' : 'Erasure'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman
                            ? 'Sie können die Löschung Ihrer Daten verlangen'
                            : 'You may request the erasure of your data'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Art. 17 DSGVO' : 'Art. 17 GDPR'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-4 py-2 font-semibold">
                          {isGerman ? 'Einschränkung' : 'Restriction'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman
                            ? 'Sie können die Einschränkung der Verarbeitung verlangen'
                            : 'You may request the restriction of processing'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Art. 18 DSGVO' : 'Art. 18 GDPR'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-4 py-2 font-semibold">
                          {isGerman ? 'Datenübertragbarkeit' : 'Data Portability'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman
                            ? 'Sie können Ihre Daten in einem strukturierten Format erhalten'
                            : 'You may receive your data in a structured, machine-readable format'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Art. 20 DSGVO' : 'Art. 20 GDPR'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-4 py-2 font-semibold">
                          {isGerman ? 'Widerspruch' : 'Objection'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman
                            ? 'Sie können der Verarbeitung widersprechen'
                            : 'You may object to the processing of your data'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Art. 21 DSGVO' : 'Art. 21 GDPR'}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-semibold">
                          {isGerman ? 'Widerruf der Einwilligung' : 'Withdrawal of Consent'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman
                            ? 'Sie können erteilte Einwilligungen jederzeit widerrufen'
                            : 'You may withdraw any consent given at any time'}
                        </td>
                        <td className="px-4 py-2">
                          {isGerman ? 'Art. 7 Abs. 3 DSGVO' : 'Art. 7(3) GDPR'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2">
                  {isGerman ? 'Ausübung Ihrer Rechte' : 'Exercising Your Rights'}
                </h3>
                <p>
                  {isGerman ? 'Zur Ausübung Ihrer Rechte kontaktieren Sie uns bitte unter:' : 'To exercise your rights, please contact us at:'}
                </p>
                <div className="ml-4 mt-2">
                  <p>
                    {isGerman ? 'E-Mail' : 'Email'}: <a href="mailto:datenschutz@storyplannerpro.de" className="text-purple-600 hover:text-purple-700">datenschutz@storyplannerpro.de</a>
                  </p>
                  <p className="mt-2">{isGerman ? 'oder schriftlich an:' : 'or by post to:'}</p>
                  <p className="mt-1">
                    Emporion Innovations GmbH<br/>
                    {isGerman ? 'Datenschutz' : 'Data Protection'}<br/>
                    Ludwigstr. 32<br/>
                    63067 Offenbach am Main
                  </p>
                </div>
                <p className="mt-2">
                  {isGerman
                    ? 'Wir werden Ihre Anfrage unverzüglich, spätestens innerhalb eines Monats bearbeiten. Bei komplexen Anfragen kann die Frist um weitere zwei Monate verlängert werden.'
                    : 'We will process your request without undue delay, and in any event within one month. For complex requests, the deadline may be extended by a further two months.'}
                </p>
              </div>
            </section>

            {/* 15. Right to Lodge a Complaint */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '15. Beschwerderecht' : '15. Right to Lodge a Complaint'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? 'Sie haben das Recht, eine Beschwerde bei einer Datenschutz-Aufsichtsbehörde einzureichen, wenn Sie der Ansicht sind, dass die Verarbeitung Ihrer personenbezogenen Daten gegen die DSGVO verstößt.'
                    : 'You have the right to lodge a complaint with a data protection supervisory authority if you believe that the processing of your personal data infringes the GDPR.'}
                </p>
                <p className="font-semibold mt-2">
                  {isGerman ? 'Zuständige Behörde für Hessen:' : 'Competent authority for Hesse:'}
                </p>
                <div className="ml-4 mt-1">
                  <p>{isGerman ? 'Der Hessische Beauftragte für Datenschutz und Informationsfreiheit' : 'The Hessian Commissioner for Data Protection and Freedom of Information'}</p>
                  <p>Postfach 3163</p>
                  <p>65021 Wiesbaden</p>
                  <p className="mt-1">
                    {isGerman ? 'Telefon' : 'Phone'}: +49 611 1408-0<br/>
                    {isGerman ? 'E-Mail' : 'Email'}: <a href="mailto:poststelle@datenschutz.hessen.de" className="text-purple-600 hover:text-purple-700">poststelle@datenschutz.hessen.de</a><br/>
                    Website:{' '}
                    <a href="https://datenschutz.hessen.de" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">
                      https://datenschutz.hessen.de
                    </a>
                  </p>
                </div>
              </div>
            </section>

            {/* 16. Data Security */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '16. Datensicherheit' : '16. Data Security'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? 'Wir setzen technische und organisatorische Sicherheitsmaßnahmen ein, um Ihre personenbezogenen Daten zu schützen:'
                    : 'We implement technical and organizational security measures to protect your personal data:'}
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>{isGerman ? 'SSL/TLS-Verschlüsselung (TLS 1.3) für alle Datenübertragungen' : 'SSL/TLS encryption (TLS 1.3) for all data transmissions'}</li>
                  <li>{isGerman ? 'Verschlüsselte Speicherung sensibler Daten (AES-256)' : 'Encrypted storage of sensitive data (AES-256)'}</li>
                  <li>{isGerman ? 'Passwörter werden nur als verschlüsselte Hashwerte gespeichert (bcrypt)' : 'Passwords are stored only as encrypted hash values (bcrypt)'}</li>
                  <li>{isGerman ? 'Zwei-Faktor-Authentifizierung für administrative Zugänge' : 'Two-factor authentication for administrative access'}</li>
                  <li>{isGerman ? 'Regelmäßige Sicherheits-Updates und Patches' : 'Regular security updates and patches'}</li>
                  <li>{isGerman ? 'Zugriffskontrolle und Berechtigungsmanagement' : 'Access control and permission management'}</li>
                  <li>{isGerman ? 'Regelmäßige Backups in zertifizierten Rechenzentren (ISO 27001)' : 'Regular backups in certified data centers (ISO 27001)'}</li>
                </ul>
                <p className="mt-2">
                  {isGerman
                    ? <>Details zu unseren technischen und organisatorischen Maßnahmen finden Sie in Anlage 1 unseres Auftragsverarbeitungsvertrags:{' '}</>
                    : <>Details of our technical and organizational measures can be found in Annex 1 of our Data Processing Agreement:{' '}</>}
                  <Link to="/avv" className="text-purple-600 hover:text-purple-700 underline">
                    https://storyplannerpro.de/avv
                  </Link>
                </p>
              </div>
            </section>

            {/* 17. No Automated Decision-Making */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '17. Keine automatisierte Entscheidungsfindung' : '17. No Automated Decision-Making'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? 'Wir setzen keine automatisierte Entscheidungsfindung einschließlich Profiling gemäß Art. 22 DSGVO ein, die Ihnen gegenüber rechtliche Wirkung entfaltet oder Sie in ähnlicher Weise erheblich beeinträchtigt.'
                    : 'We do not employ automated decision-making, including profiling, pursuant to Art. 22 GDPR that produces legal effects concerning you or similarly significantly affects you.'}
                </p>
              </div>
            </section>

            {/* 18. Social Media */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                18. Social Media
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? 'Wir unterhalten Präsenzen in sozialen Netzwerken. Wenn Sie unsere Social-Media-Seiten besuchen, gelten die Datenschutzbestimmungen der jeweiligen Plattform:'
                    : 'We maintain presences on social networks. When you visit our social media pages, the respective platform\'s privacy policy applies:'}
                </p>
                <ul className="list-disc list-inside ml-4">
                  <li>
                    Instagram:{' '}
                    <a href="https://help.instagram.com/519522125107875" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">
                      https://help.instagram.com/519522125107875
                    </a>
                  </li>
                </ul>
                <p className="mt-2">
                  {isGerman
                    ? 'Wir haben keinen Einfluss auf die Datenverarbeitung durch die Plattformbetreiber.'
                    : 'We have no control over data processing by the platform operators.'}
                </p>
              </div>
            </section>

            {/* 19. Changes */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '19. Änderungen dieser Datenschutzerklärung' : '19. Changes to This Privacy Policy'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? 'Wir behalten uns vor, diese Datenschutzerklärung anzupassen, um sie an geänderte Rechtslagen oder Änderungen unserer Dienste anzupassen. Die aktuelle Version finden Sie stets unter:'
                    : 'We reserve the right to amend this privacy policy to reflect changes in the legal framework or modifications to our services. The current version is always available at:'}
                </p>
                <p className="ml-4">
                  <Link to={isGerman ? '/datenschutz' : '/privacy'} className="text-purple-600 hover:text-purple-700 underline">
                    https://storyplannerpro.de/datenschutz
                  </Link>
                </p>
                <p className="mt-2">
                  {isGerman
                    ? 'Bei wesentlichen Änderungen, die Ihre Rechte betreffen, werden wir Sie – soweit möglich – gesondert informieren.'
                    : 'In the event of material changes affecting your rights, we will inform you separately where possible.'}
                </p>
              </div>
            </section>

            {/* Footer info */}
            <section className="border-t border-gray-200 pt-6">
              <div className="text-gray-700 text-sm space-y-2">
                <p className="text-xs text-gray-500">
                  {isGerman ? 'Stand: Januar 2025' : 'Last updated: January 2025'}
                </p>
                <div className="mt-2">
                  <p className="font-semibold">Emporion Innovations GmbH</p>
                  <p>Ludwigstr. 32</p>
                  <p>63067 Offenbach am Main, {isGerman ? 'Deutschland' : 'Germany'}</p>
                  <p className="mt-1">
                    {isGerman ? 'E-Mail' : 'Email'}: <a href="mailto:datenschutz@storyplannerpro.de" className="text-purple-600 hover:text-purple-700">datenschutz@storyplannerpro.de</a><br/>
                    Web: <a href="https://storyplannerpro.de" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">https://storyplannerpro.de</a>
                  </p>
                </div>
              </div>
            </section>

          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center">
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600">
            <Link to={isGerman ? '/impressum' : '/imprint'} className="hover:text-purple-600">
              {isGerman ? 'Impressum' : 'Imprint'}
            </Link>
            <Link to={isGerman ? '/agb' : '/terms'} className="hover:text-purple-600">
              {isGerman ? 'AGB' : 'Terms of Service'}
            </Link>
            <Link to="/contact" className="hover:text-purple-600">
              {isGerman ? 'Kontakt' : 'Contact'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPage
